import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Bot, BarChart3, TrendingUp, Calculator, Shield, ExternalLink } from 'lucide-react';
import { products } from '@/lib/siteConfig';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.1 } }
};

const iconMap = {
  'asksara': Bot,
  'bizhub': BarChart3,
  'letstalkgrowth': TrendingUp,
  'letstalkstrategy': Calculator,
  'letstalkrelief': Shield
};

export const Innovation = () => {
  return (
    <section 
      id="innovation"
      className="bg-[#0B2240] text-white py-16 lg:py-20"
      data-testid="innovation-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.18em] text-xs text-[#E3C96E] mb-3">
            INNOVATION BY KGOB
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold">
            Where technology meets trusted advisory.
          </h2>
          <p className="mt-4 text-lg text-white/80 max-w-3xl mx-auto">
            Each platform we create begins with a single purpose — to help clients and peers achieve clarity, compliance, and confidence through smarter tools.
          </p>
        </motion.div>

        <motion.div 
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
        >
          {(products || []).map((product) => {
            const Icon = iconMap[product.slug] || Bot;
            return (
              <motion.div key={product.slug} variants={fadeUp}>
                <Card 
                  className="rounded-2xl bg-white/5 border border-white/10 p-6 h-full hover:bg-white/8 hover:border-white/20 hover:shadow-[0_12px_40px_rgba(11,34,64,.24)] transition-all duration-300 group"
                  data-testid={`innovation-${product.slug}-card`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                      <Icon className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <Badge 
                      variant="secondary" 
                      className="text-xs uppercase tracking-wider bg-[#1FA3A3]/10 text-[#1FA3A3] border-[#1FA3A3]/20 hover:bg-[#1FA3A3]/20"
                    >
                      {product.tag}
                    </Badge>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-2">
                    {product.url && product.url !== '#' ? (
                      <a
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 hover:text-[#E3C96E] transition-colors underline decoration-[#D4AF37]/40 underline-offset-4 hover:decoration-[#E3C96E]"
                        data-testid={`innovation-${product.slug}-link`}
                      >
                        {product.name}
                        <ExternalLink className="w-4 h-4 text-[#D4AF37]" />
                      </a>
                    ) : (
                      product.name
                    )}
                  </h3>
                  <p className="text-sm text-[#E3C96E] mb-3 font-medium">
                    {product.tagline}
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed">
                    {product.desc}
                  </p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Closing Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-16 pt-12 border-t border-white/10"
        >
          <p className="font-['Playfair_Display'] text-xl sm:text-2xl text-white/90 italic max-w-3xl mx-auto mb-3">
            "Innovation is how we keep our promise — clarity, delivered differently."
          </p>
          <p className="text-sm text-[#E3C96E] font-medium">
            Sara Gonzalez, CPA, Managing Partner
          </p>
        </motion.div>
      </div>
    </section>
  );
};